/**
 * Operator API Service
 * Page-specific API handler for operator management
 * Uses the global apiService for all requests
 */

import { apiGet, apiPost, apiPut, apiDelete, API_ENDPOINTS } from './apiService'
import { showSuccess, showError } from '../utils/toast'

export const operatorApi = {
  /**
   * Get operator metrics
   */
  async getMetrics(options = {}) {
    try {
      return await apiGet(API_ENDPOINTS.OPERATOR.METRICS)
    } catch (error) {
      if (!options.silent) {
        showError(error.message || 'Failed to load metrics')
      }
      throw error
    }
  },

  /**
   * Get customers list with pagination and filters
   */
  async getCustomers(params = {}, options = {}) {
    try {
      return await apiGet(API_ENDPOINTS.OPERATOR.CUSTOMERS, params)
    } catch (error) {
      if (!options.silent) {
        showError(error.message || 'Failed to load customers')
      }
      throw error
    }
  },

  /**
   * Get customer detail
   */
  async getCustomerDetail(orgId) {
    try {
      return await apiGet(API_ENDPOINTS.OPERATOR.CUSTOMER_DETAIL(orgId))
    } catch (error) {
      showError(error.message || 'Failed to load customer details')
      throw error
    }
  },

  /**
   * Extend trial period
   */
  async extendTrial(orgId, days) {
    try {
      const result = await apiPost(API_ENDPOINTS.OPERATOR.EXTEND_TRIAL(orgId), { days })
      showSuccess(`Trial extended by ${days} days`)
      return result
    } catch (error) {
      showError(error.message || 'Failed to extend trial')
      throw error
    }
  },

  /**
   * Mark customer as active
   */
  async markActive(orgId) {
    try {
      const result = await apiPost(API_ENDPOINTS.OPERATOR.MARK_ACTIVE(orgId))
      showSuccess('Customer marked as active')
      return result
    } catch (error) {
      showError(error.message || 'Failed to mark customer as active')
      throw error
    }
  },

  /**
   * Reset BillJean data for one customer
   */
  async resetCustomer(orgId) {
    try {
      const result = await apiPost(API_ENDPOINTS.OPERATOR.RESET_CUSTOMER(orgId))
      showSuccess(result?.message || 'BillJean data was reset. Setup will start again.')
      return result
    } catch (error) {
      showError(error.message || 'Failed to reset BillJean data')
      throw error
    }
  },

  /**
   * Update customer notes
   */
  async updateNotes(orgId, notes) {
    try {
      const result = await apiPut(API_ENDPOINTS.OPERATOR.UPDATE_NOTES(orgId), { notes })
      showSuccess('Notes updated successfully')
      return result
    } catch (error) {
      showError(error.message || 'Failed to update notes')
      throw error
    }
  },

  // Operator Management APIs
  /**
   * Get operators list with pagination
   */
  async getOperators(params = {}) {
    try {
      return await apiGet(API_ENDPOINTS.OPERATOR_MANAGEMENT.LIST, params)
    } catch (error) {
      showError(error.message || 'Failed to load operators')
      throw error
    }
  },

  /**
   * Get operator by ID
   */
  async getOperator(id) {
    try {
      return await apiGet(API_ENDPOINTS.OPERATOR_MANAGEMENT.DETAIL(id))
    } catch (error) {
      showError(error.message || 'Failed to load operator')
      throw error
    }
  },

  /**
   * Create new operator
   */
  async createOperator(data) {
    try {
      const result = await apiPost(API_ENDPOINTS.OPERATOR_MANAGEMENT.CREATE, data)
      showSuccess('Operator created successfully')
      return result
    } catch (error) {
      // If there are field-specific errors, don't show toast (form will handle it)
      // Otherwise show the error message
      if (!error.errors || Object.keys(error.errors).length === 0) {
        const errorMessage = error.message || 'Failed to create operator'
        showError(errorMessage)
      }
      // Always re-throw so form can handle field-specific errors
      throw error
    }
  },

  /**
   * Update operator
   */
  async updateOperator(id, data) {
    try {
      const result = await apiPut(API_ENDPOINTS.OPERATOR_MANAGEMENT.UPDATE(id), data)
      showSuccess('Operator updated successfully')
      return result
    } catch (error) {
      // If there are field-specific errors, don't show toast (form will handle it)
      // Otherwise show the error message
      if (!error.errors || Object.keys(error.errors).length === 0) {
        const errorMessage = error.message || 'Failed to update operator'
        showError(errorMessage)
      }
      // Always re-throw so form can handle field-specific errors
      throw error
    }
  },

  /**
   * Delete operator
   */
  async deleteOperator(id) {
    try {
      const result = await apiDelete(API_ENDPOINTS.OPERATOR_MANAGEMENT.DELETE(id))
      showSuccess('Operator deleted successfully')
      return result
    } catch (error) {
      showError(error.message || 'Failed to delete operator')
      throw error
    }
  },

  /**
   * Toggle operator status
   */
  async toggleOperatorStatus(id) {
    try {
      const result = await apiPost(API_ENDPOINTS.OPERATOR_MANAGEMENT.TOGGLE_STATUS(id))
      showSuccess('Operator status updated')
      return result
    } catch (error) {
      showError(error.message || 'Failed to update operator status')
      throw error
    }
  },
}
